/**
 * FlowApp — style objects.
 *
 * Common patterns (group cards, steppers, transport, buttons, toasts, etc.)
 * are imported from editorSharedStyles and re-exported so existing imports in
 * FlowApp.tsx don't need to change.  Only Flow-specific styles live here.
 */

import React from 'react';
import { color, font } from '../../components/ui/theme';

// ── Re-export everything shared ───────────────────────────────────────────────
export {
  shellStyle,
  leftPanelStyle,
  topScrollStyle,
  rightPanelStyle,
  sidebarScrollStyle,
  centerStyle,
  groupLabelStyle,
  editorBtnState as flowBtnState,
  stepperStyle,
  stepperLabelStyle,
  stepperPillStyle,
  stepperBtnStyle,
  stepperValueStyle,
  stepperInputStyle,
  autoRowStyle,
  autoBtnStyle,
  cameraGridStyle,
  cameraBtnStyle,
  cameraActionBtnStyle,
  bottomBtnsStyle,
  uniformBtn,
  mapBtnVariant,
  mapBtnVariant as saveBtnVariant,
  mapBtnVariant as backBtnVariant,
  mapBtnVariant as playOnPhoneBtnVariant,
  mapBtnVariant as loadBtnVariant,
  mapBtnVariant as newMapBtnVariant,
  clearBtnVariant,
  dropOverlayStyle as chartDropOverlayStyle,
  dropCardStyle as chartDropCardStyle,
  transportStyle,
  transportBtn,
  timeReadout,
  beatReadout,
  popupOverlayStyle,
  popupWindowStyle,
  popupTitleBarStyle,
} from '../../components/ui/editorSharedStyles';

// Flow panel card — same as the shared group card but WITHOUT the accent
// border and drop shadow. The user wanted that visible "frame" (border + glow)
// gone, so each panel reads as a clean dark frosted block with no outline.
export const groupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  padding: '7px 9px',
  background: 'oklch(0.065 calc(0.016 * var(--tint-c)) var(--tint-h) / 0.62)',
  // Very subtle outline — same disguised tint as the inner containers
  // (stepper pills), not the old bright accent border.
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.15)',
  borderRadius: 16,
  backdropFilter: 'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
};

// bottomSectionStyle — identical to shared but Flow adds marginTop: 10 so the
// pinned footer doesn't sit flush against the last scrollable group.
export const bottomSectionStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  padding: 10,
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.6)',
  border: '1.5px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.35)',
  borderRadius: 12,
  marginTop: 10,
};

// toastStyle — shared base + Flow-specific anchor position.
export const toastStyle: React.CSSProperties = {
  position: 'fixed',
  left: 250,
  bottom: 150,
  padding: '8px 14px',
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.6,
  fontFamily: font.sans,
  pointerEvents: 'none',
  zIndex: 4000,
  whiteSpace: 'nowrap',
  boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.92)',
  border: '1px solid rgba(34, 197, 94, 0.4)',
  color: '#A7F3D0',
};

export const toastErrorStyle: React.CSSProperties = {
  ...toastStyle,
  border: '1px solid rgba(239, 68, 68, 0.4)',
  color: '#FCA5A5',
};

// ── Flow-specific: Highway backdrop ──────────────────────────────────────────

export const highwayHolder: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  position: 'relative',
};

// The 3D backdrop gradient now lives in the shared theme module so every editor
// renders it identically. Re-exported here for back-compat with Flow imports.
export { bg3dGradient, bg3dLayerStyle } from '../../components/ui/theme';

export const offsetWarnStyle: React.CSSProperties = {
  position: 'absolute',
  top: '12%',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 20,
  padding: '10px 18px',
  borderRadius: 10,
  background: 'rgba(10, 6, 25, 0.9)',
  border: '1.5px solid rgba(248, 113, 113, 0.55)',
  color: '#FCA5A5',
  fontSize: 14,
  fontWeight: 700,
  fontFamily: font.sans,
  letterSpacing: 0.3,
  pointerEvents: 'none',
  boxShadow: '0 8px 28px rgba(0,0,0,0.5)',
  whiteSpace: 'nowrap',
};

// ── Flow-specific: Debug panel ────────────────────────────────────────────────

export const debugBodyStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 11,
  color: '#E8E6F5',
  whiteSpace: 'pre',
};

export const debugBtnStyle: React.CSSProperties = {
  background: 'rgba(34, 211, 238, 0.15)',
  border: '1px solid rgba(34, 211, 238, 0.4)',
  color: '#22D3EE',
  borderRadius: 5,
  padding: '2px 10px',
  fontSize: 11,
  fontFamily: font.sans,
  fontWeight: 600,
  cursor: 'pointer',
};

export const debugStatsRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 6,
  margin: '2px 0 8px',
};

export const debugStatStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 2,
  padding: '6px 4px',
  background: 'rgba(20, 14, 40, 0.7)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.22)',
  borderRadius: 6,
};

export const debugStatLabel: React.CSSProperties = {
  fontSize: 8,
  letterSpacing: 0.6,
  color: color.inkFaint,
  fontFamily: font.sans,
  fontWeight: 700,
};

export const debugStatVal: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 800,
  fontFamily: 'monospace',
};

// ── Flow-specific: Note brush palette ────────────────────────────────────────

export const lanesRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-around',
  padding: '4px 8px',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.5)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.15)',
  borderRadius: 8,
};

export const laneSwatchStyle: React.CSSProperties = {
  width: 22,
  height: 22,
  borderRadius: 4,
  border: '1px solid rgba(255,255,255,0.15)',
};

export const brushRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 6,
};

export const brushCardStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 2,
  padding: '8px 8px 7px',
  borderRadius: 8,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.15)',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.5)',
  cursor: 'pointer',
  fontFamily: font.sans,
  textAlign: 'left',
  transition: 'all 0.15s ease',
};

export const brushCardTitleStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.8,
};

export const brushCardDescStyle: React.CSSProperties = {
  fontSize: 9,
  color: color.inkDim,
  lineHeight: 1.3,
  fontWeight: 400,
};

export const dirNoteRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 6,
};

export const dirNoteBtnStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
  padding: '8px 4px 6px',
  borderRadius: 8,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.15)',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.5)',
  cursor: 'pointer',
  fontFamily: font.sans,
  transition: 'all 0.15s ease',
};

export const dirNoteGlyphStyle: React.CSSProperties = {
  fontSize: 16,
  lineHeight: 1,
  fontWeight: 700,
};

export const dirNoteLabelStyle: React.CSSProperties = {
  fontSize: 9,
  letterSpacing: 0.6,
  fontWeight: 600,
  color: color.inkDim,
};

export const brushBtnStyle: React.CSSProperties = {
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.15)',
  borderRadius: 6,
  padding: '5px 4px',
  fontSize: 10,
  fontWeight: 600,
  cursor: 'pointer',
  letterSpacing: 0.5,
  fontFamily: font.sans,
  transition: 'all 0.15s ease',
};

// ── Flow-specific: BPM popup ──────────────────────────────────────────────────

export const bpmApplyBtnStyle: React.CSSProperties = {
  padding: '8px 18px',
  borderRadius: 999,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.5)',
  background: 'oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.18)',
  color: color.ink,
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: font.sans,
};

export const bpmCancelBtnStyle: React.CSSProperties = {
  marginTop: 12,
  padding: '8px 16px',
  borderRadius: 999,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.2)',
  background: 'transparent',
  color: '#8A82B0',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: font.sans,
};

export const bpmPopupBackdrop: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 10000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'oklch(0 0 0 / 0.55)',
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
};

// Matches the shared new popup look (popupWindowStyle): surface-panel glass +
// accent ring + accent glow.
export const bpmPopupCard: React.CSSProperties = {
  minWidth: 300,
  maxWidth: 420,
  padding: '26px 28px',
  borderRadius: 18,
  // Glass: translucent surface so the blur shows the scene through it.
  background: 'oklch(0.11 calc(0.02 * var(--tint-c)) var(--tint-h) / 0.72)',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.25)',
  boxShadow: '0 12px 48px rgba(0,0,0,0.6), 0 0 30px oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.10)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  textAlign: 'center',
  color: color.ink,
  fontFamily: font.sans,
};

export const bpmPopupSpinner: React.CSSProperties = {
  fontSize: 30,
  marginBottom: 10,
};

export const bpmPopupTitle: React.CSSProperties = {
  fontSize: 17,
  fontWeight: 700,
  letterSpacing: 0.3,
};

export const bpmPopupSub: React.CSSProperties = {
  marginTop: 8,
  fontSize: 12,
  color: color.inkDim,
  lineHeight: 1.4,
};

export const bpmPopupSub2: React.CSSProperties = {
  marginTop: 12,
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--accent)',
};

export const bpmPopupRow: React.CSSProperties = {
  marginTop: 14,
  display: 'flex',
  gap: 10,
  justifyContent: 'center',
};

// ── Flow-specific: Audio / chart loading ──────────────────────────────────────

export const loadAudioBtnStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.18), oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.12))',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.3)',
  color: 'var(--accent-bright)',
  padding: '8px 10px',
  borderRadius: 8,
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: font.sans,
  letterSpacing: 0.5,
  textAlign: 'center',
  width: '100%',
};

export const audioNameStyle: React.CSSProperties = {
  fontSize: 10,
  color: color.inkDim,
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.5)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.15)',
  padding: '4px 8px',
  borderRadius: 6,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const audioErrorStyle: React.CSSProperties = {
  fontSize: 10,
  color: '#F87171',
  background: 'rgba(248, 113, 113, 0.08)',
  border: '1px solid rgba(248, 113, 113, 0.18)',
  padding: '4px 8px',
  borderRadius: 6,
  lineHeight: 1.4,
};

export const loadChartBtnStyle: React.CSSProperties = {
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.5)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.2)',
  color: color.inkDim,
  padding: '8px 10px',
  borderRadius: 8,
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: font.sans,
  letterSpacing: 0.5,
  textAlign: 'center',
  width: '100%',
};

export const chartStatusStyle: React.CSSProperties = {
  fontSize: 10,
  color: '#34D399',
  background: 'rgba(52, 211, 153, 0.08)',
  border: '1px solid rgba(52, 211, 153, 0.18)',
  padding: '4px 8px',
  borderRadius: 6,
  lineHeight: 1.4,
};

export const chartErrorStyle: React.CSSProperties = {
  fontSize: 10,
  color: '#F87171',
  background: 'rgba(248, 113, 113, 0.08)',
  border: '1px solid rgba(248, 113, 113, 0.18)',
  padding: '4px 8px',
  borderRadius: 6,
  lineHeight: 1.4,
};

// ── Flow-specific: Difficulty selector ───────────────────────────────────────

export const diffGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 4,
};

export const diffBtnStyle: React.CSSProperties = {
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.15)',
  borderRadius: 999,
  padding: '6px 12px',
  fontSize: 10,
  fontWeight: 600,
  cursor: 'pointer',
  letterSpacing: 0.5,
  fontFamily: font.sans,
  transition: 'all 0.15s ease',
  // Single row: difficulty name + a small count pill (compact panel).
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
};

// Note-count badge tucked inside the difficulty pill.
export const diffCountPillStyle: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1,
  padding: '2px 6px',
  borderRadius: 999,
  background: 'oklch(0.10 calc(0.02 * var(--tint-c)) var(--tint-h) / 0.65)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
};

export const diffStatsStyle: React.CSSProperties = {
  fontSize: 10,
  color: color.inkFaint,
  textAlign: 'center',
  marginTop: 4,
};

// ── Flow-specific: Help bar ───────────────────────────────────────────────────

export const helpBarStyle: React.CSSProperties = {
  // `relative` anchors the expandable "All shortcuts" sheet above the bar.
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '5px 14px',
  background: 'oklch(0.065 calc(0.016 * var(--tint-c)) var(--tint-h) / 0.78)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
  borderRadius: 999,
  color: 'var(--tx-hi)',
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.2,
  lineHeight: 1.3,
  fontFamily: font.sans,
};

// ── Flow-specific: Transport readouts ────────────────────────────────────────

export const chartMetaStyle: React.CSSProperties = {
  color: color.inkFaint,
  fontSize: 11,
  letterSpacing: 0.5,
};

// ── Visualizer group ──────────────────────────────────────────────────────────

export const vizGroupBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  width: '100%',
  height: 32,
  padding: '0 14px',
  // Standard inactive surface (matches the other sidebar buttons) — without
  // this the native <button> shows a white background.
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
  color: color.accentSoft,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.18)',
  borderRadius: 999,
  cursor: 'pointer',
  fontFamily: font.sans,
  fontSize: 12,
  fontWeight: 600,
  transition: 'all 0.15s ease',
  boxSizing: 'border-box',
};

export const vizChipStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '5px 6px 5px 12px',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.55)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.22)',
  borderRadius: 999,
  overflow: 'hidden',
};

// "React to" segmented control (Audio | Notes).
export const reactSegStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  padding: 3,
  borderRadius: 999,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.18)',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
};

export const reactSegLabelStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: 0.4,
  color: color.inkFaint,
  padding: '0 6px',
  whiteSpace: 'nowrap',
};

export const reactSegBtnStyle: React.CSSProperties = {
  flex: 1,
  height: 24,
  borderRadius: 999,
  border: '1px solid transparent',
  background: 'transparent',
  color: color.inkFaint,
  fontFamily: font.sans,
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
};

export const reactSegBtnActiveStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h)/0.25), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h)/0.15))',
  borderColor: 'oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h)/0.5)',
  color: 'var(--accent)',
};

export const vizChipNameStyle: React.CSSProperties = {
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 11,
  color: color.inkDim,
  fontFamily: font.sans,
};

export const vizChipRemoveStyle: React.CSSProperties = {
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  padding: 0,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.22)',
  borderRadius: '50%',
  background: 'transparent',
  color: color.inkFaint,
  cursor: 'pointer',
};
